import React, { useEffect, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { FiEdit, FiPlus, FiTrash2, FiX } from "react-icons/fi";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  createTourGallerySet,
  deleteTourGallerySet,
  fetchAvailableTourGalleryThings,
  fetchTourGallerySets,
  updateTourGallerySet,
} from "../../../services/adminRestapiService";
import TopBar from "../../common/TopBar";
import Sidebar from "../../common/Sidebar";
import { getImageUploadError, IMAGE_UPLOAD_ACCEPT, IMAGE_UPLOAD_HELP } from "../../../utils/imageUpload";

const API_URL = "https://api.gosehari.com";
const MAX_IMAGES = 4;
const emptyImageSlots = () => Array(MAX_IMAGES).fill(null);
const emptyGallery = () => ({ thing_id: "", images: emptyImageSlots() });

const getImageUrl = (imagePath) => imagePath?.startsWith("http") || imagePath?.startsWith("blob:") ? imagePath : `${API_URL}${imagePath}`;

function TourGalleryImages() {
  const [galleries, setGalleries] = useState([]);
  const [availableThings, setAvailableThings] = useState([]);
  const [canAdd, setCanAdd] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGallery, setEditingGallery] = useState(null);
  const [form, setForm] = useState(emptyGallery);
  const [imageFiles, setImageFiles] = useState(emptyImageSlots());
  const [saving, setSaving] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const token = localStorage.getItem("authToken") || localStorage.getItem("token");

  const toggleSidebar = () => setSidebarOpen((isCurrentlyOpen) => !isCurrentlyOpen);

  const loadGalleries = async () => {
    setLoading(true);
    try {
      setGalleries((await fetchTourGallerySets(token)).data.data || []);
    } catch {
      toast.error("Failed to load Tour Galleries");
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableThings = async () => {
    try {
      setAvailableThings((await fetchAvailableTourGalleryThings(token)).data.data || []);
    } catch {
      setAvailableThings([]);
      toast.error("Failed to load available Top Things");
    }
  };

  const refreshAddAvailability = async () => {
    try {
      const things = (await fetchAvailableTourGalleryThings(token)).data.data || [];
      setCanAdd(things.some((thing) => !thing.is_mapped));
    } catch {
      setCanAdd(false);
    }
  };

  useEffect(() => {
    loadGalleries();
    refreshAddAvailability();
  }, []);

  const openAdd = async () => {
    await loadAvailableThings();
    setEditingGallery(null);
    setForm(emptyGallery());
    setImageFiles(emptyImageSlots());
    setDialogOpen(true);
  };

  const openEdit = async (gallery) => {
    await loadAvailableThings();
    setEditingGallery(gallery);
    setForm({
      thing_id: String(gallery.thing_id),
      images: Array.from({ length: MAX_IMAGES }, (_, index) => gallery.images?.[index] || null),
    });
    setImageFiles(emptyImageSlots());
    setDialogOpen(true);
  };

  const clearImageSlot = (index) => {
    setForm((current) => ({
      ...current,
      images: current.images.map((image, imageIndex) => imageIndex === index ? null : image),
    }));
    setImageFiles((current) => current.map((file, fileIndex) => fileIndex === index ? null : file));
  };

  const selectImage = (index, file, input) => {
    const imageError = getImageUploadError(file);
    if (imageError) {
      toast.error(imageError);
      input.value = "";
      return;
    }
    setImageFiles((current) => current.map((currentFile, fileIndex) => fileIndex === index ? (file || null) : currentFile));
  };

  const submit = async () => {
    if (!form.thing_id) return toast.error("Select a Top Thing");
    if (!form.images.some(Boolean) && !imageFiles.some(Boolean)) {
      return toast.error("Upload at least one gallery image");
    }

    setSaving(true);
    try {
      const payload = new FormData();
      payload.append("thing_id", form.thing_id);
      payload.append("existing_images", JSON.stringify(form.images));
      payload.append(
        "slot_indexes",
        JSON.stringify(imageFiles.map((file, index) => file ? index : null).filter((index) => index !== null))
      );
      imageFiles.forEach((file) => {
        if (file) payload.append("images", file);
      });

      if (editingGallery) await updateTourGallerySet(token, editingGallery.thing_id, payload);
      else await createTourGallerySet(token, payload);

      toast.success(editingGallery ? "Tour Gallery updated" : "Tour Gallery created");
      setDialogOpen(false);
      await Promise.all([loadGalleries(), refreshAddAvailability()]);
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to save Tour Gallery");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (gallery) => {
    if (!window.confirm(`Delete every gallery image for “${gallery.thing_title}”?`)) return;
    try {
      await deleteTourGallerySet(token, gallery.thing_id);
      toast.success("Tour Gallery deleted; its Top Thing is available again");
      await Promise.all([loadGalleries(), refreshAddAvailability()]);
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to delete Tour Gallery");
    }
  };

  const columns = [
    { field: "thing_id", headerName: "Top Thing ID", width: 105 },
    { field: "thing_title", headerName: "Top Thing", flex: 1.3, minWidth: 200 },
    { field: "thing_category", headerName: "Category", width: 140 },
    {
      field: "images",
      headerName: "Gallery images (4 slots)",
      width: 335,
      sortable: false,
      renderCell: ({ value = [], row }) => (
        <div className="flex h-full items-center gap-1">
          {Array.from({ length: MAX_IMAGES }, (_, index) => value[index] ? (
            <img key={index} src={getImageUrl(value[index])} alt={`${row.thing_title} ${index + 1}`} className="h-14 w-14 rounded-lg border border-slate-200 object-cover" />
          ) : (
            <div key={index} className="flex h-14 w-14 items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 text-[10px] text-slate-400">Empty</div>
          ))}
        </div>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 125,
      sortable: false,
      renderCell: ({ row }) => (
        <Box>
          <IconButton aria-label="Edit Tour Gallery" color="primary" onClick={() => openEdit(row)}><FiEdit /></IconButton>
          <IconButton aria-label="Delete Tour Gallery" color="error" onClick={() => remove(row)}><FiTrash2 /></IconButton>
        </Box>
      ),
    },
  ];

  const hasAvailableThings = availableThings.some((thing) => !thing.is_mapped);
  const selectedTopThing = availableThings.find((thing) => String(thing.id) === String(form.thing_id));

  return (
    <div className="flex min-h-screen bg-slate-50">
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className={`min-h-screen min-w-0 flex-1 transition-[margin] duration-300 ${sidebarOpen ? "lg:ml-64" : "lg:ml-20"}`}>
        <TopBar toggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} />

        <main className="space-y-6 px-4 pb-8 pt-24 sm:px-6 lg:px-8">
          <section className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm sm:p-5">
            <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">Tour Gallery</h1>
                <p className="mt-1 text-sm text-slate-600">Manage up to four ordered gallery images for each Top Thing.</p>
                {!loading && !canAdd && <p className="mt-1 text-sm text-amber-700">All Top Things already have a Tour Gallery.</p>}
              </div>
              <Button variant="contained" startIcon={<FiPlus />} onClick={openAdd} disabled={loading || !canAdd} className="self-start sm:self-auto">
                Add Tour Gallery
              </Button>
            </div>

            <div className="min-w-0 overflow-x-auto rounded-xl border border-slate-100">
              <DataGrid rows={galleries} columns={columns} loading={loading} autoHeight rowHeight={68} disableRowSelectionOnClick pageSizeOptions={[10, 25, 50]} initialState={{ pagination: { paginationModel: { pageSize: 10 } } }} sx={{ minWidth: 820, border: 0 }} />
            </div>
          </section>
        </main>
      </div>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editingGallery ? "Edit Tour Gallery" : "Add Tour Gallery"}</DialogTitle>
        <DialogContent>
          {!editingGallery && !hasAvailableThings ? (
            <p className="py-4 text-slate-700">All Top Things already have a Tour Gallery.</p>
          ) : (
            <Box component="form" className="flex flex-col gap-4 pt-3">
              {editingGallery ? (
                <div>
                  <span className="mb-1 block text-sm">Top Thing</span>
                  <div className="w-full rounded-lg border border-slate-200 bg-slate-50 p-3 text-slate-700">{editingGallery.thing_title}</div>
                </div>
              ) : (
                <label>
                  <span className="mb-1 block text-sm">Top Thing *</span>
                  <select name="thing_id" value={form.thing_id} onChange={(event) => setForm((current) => ({ ...current, thing_id: event.target.value }))} className="w-full rounded-lg border border-slate-300 p-3" required>
                    <option value="">Select a Top Thing</option>
                    {availableThings.map((thing) => <option key={thing.id} value={thing.id} disabled={thing.is_mapped}>{thing.title} ({thing.category}){thing.is_mapped ? " — Gallery already added" : ""}</option>)}
                  </select>
                </label>
              )}

              {selectedTopThing && !editingGallery && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <p className="font-semibold text-slate-800">{selectedTopThing.title}</p>
                  <p className="mt-1 text-sm text-slate-500">{selectedTopThing.category}</p>
                  <p className="mt-1 text-sm text-slate-600">{selectedTopThing.description || "No description available."}</p>
                </div>
              )}

              <div>
                <p className="mb-2 text-sm">Gallery images (up to {MAX_IMAGES})</p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {Array.from({ length: MAX_IMAGES }, (_, index) => {
                    const preview = imageFiles[index] ? URL.createObjectURL(imageFiles[index]) : form.images[index];
                    return (
                      <div key={index} className="rounded-lg border border-slate-200 p-2 text-sm">
                        <div className="mb-1 flex items-center justify-between"><span>Image {index + 1}</span>{preview && <button type="button" aria-label={`Remove image ${index + 1}`} onClick={() => clearImageSlot(index)} className="rounded p-1 text-slate-500 transition hover:bg-rose-50 hover:text-rose-600"><FiX /></button>}</div>
                        {preview ? <img src={getImageUrl(preview)} alt={`Gallery ${index + 1}`} className="h-28 w-full rounded-md object-cover" /> : <div className="flex h-28 items-center justify-center rounded-md border border-dashed border-slate-300 bg-slate-50 text-slate-400">Empty image slot</div>}
                        <input className="mt-2 block w-full" type="file" accept={IMAGE_UPLOAD_ACCEPT} onChange={(event) => selectImage(index, event.target.files[0], event.target)} />
                      </div>
                    );
                  })}
                </div>
              </div>
              <p className="text-xs text-slate-500">Images are displayed on the trip page in slot order, from 1 to {MAX_IMAGES}. {IMAGE_UPLOAD_HELP}</p>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          {(editingGallery || hasAvailableThings) && <Button variant="contained" onClick={submit} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>}
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default TourGalleryImages;
